import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, onPromptSummary, onTokenUsage } from "../../api/client";
import { useCurrentScene } from "../../api/hooks/useCurrentScene";
import { usePerformAction } from "../../api/hooks/usePerformAction";
import { useSceneStream } from "../../api/hooks/useSceneStream";
import { canUseReaderCache } from "../../api/reader-envelope";
import { useScenario } from "../../api/hooks/useScenario";
import { useCharacterUnlock } from "../../api/hooks/useCharacterUnlock";
import type { components } from "../../api/schema";
import { loadClientEnv } from "../../config/env";
import { mapProblemDetail } from "../../errors/mapProblemDetail";
import { playlineStorage } from "../../playline/storage";
import {
  buildScenarioPlayPath,
  resolveScenarioRouteFromLocation,
} from "../../utils/scenarioPlayRoute";
import { BackscrollView } from "../worldline/BackscrollView";
import { WorldlineTreeView } from "../worldline/WorldlineTreeView";
import { PalimpsestModal } from "../worldline/palimpsest/PalimpsestModal";
import { CharacterCodexView } from "../character/CharacterCodexView";
import { UnlockToast } from "../character/UnlockToast";
import { DeductionHost } from "../deduction";
import { DebugOverlay } from "../debug/DebugOverlay";
import { CurrentBeatIndicator } from "../../components/beat/CurrentBeatIndicator";
import { useReaderStylePresetContext } from "../reader-style/ReaderStylePresetProvider";
import { useModelSelector } from "../debug/useModelSelector";
import { ScenarioStart } from "../scenario-start/ScenarioStart";
import { AutoResumePrompt } from "../playline-persistence/AutoResumePrompt";
import { usePlaylineSync } from "../playline-persistence/usePlaylineSync";
import { ConflictResolutionModal } from "../../components/playline-persistence/ConflictResolutionModal";
import { usePlaylineDebugState } from "./usePlaylineDebugState";
import { useToggle } from "../../components/ui/useToggle";
import { SceneSummaryFeed } from "../../components/scenario/SceneSummaryFeed";
import { ScenarioLineagePanel } from "../../components/scenario/ScenarioLineagePanel";
import { ModalFrame } from "../../components/ui/ModalFrame";
import { ReportModal } from "../../components/reader-report/ReportModal";
import { EndingView } from "./EndingView";
import { ErrorBanner } from "./ErrorBanner";
import { LoadingOverlay } from "./LoadingOverlay";
import { ScenarioIntroOverlay } from "./ScenarioIntroOverlay";
import { SceneView } from "./SceneView";
import { StreamingSceneView } from "./StreamingSceneView";
import {
  FixedContentUnavailable,
  isFixedReaderReason,
  ReaderGenerationMode,
} from "./ReaderPlaybackStatus";
import {
  VisualNovelLayout,
  type VisualNovelActorCue,
  type VisualNovelCutinActor,
  type VisualNovelMarionActor,
} from "./VisualNovelLayout";
import { useSceneBackground } from "./useSceneBackground";
import {
  resolveChoiceThinkingCue,
  type ChoiceListChoice,
  type ChoicePreview,
  type ChoiceVisualCue,
} from "./ChoiceList";
import { stripInlineMarkup } from "./parseInlineMarkup";
import { resolveChoicePrompt } from "./resolveChoicePrompt";
import {
  inferMarionManifestUrl,
  normalizeActingVariantId,
  parseMarionStillPath,
  resolveCutinImageUrl,
  resolveMarionStandingRenderConfig,
  resolveVariantStandingImageUrl,
} from "./characterAssetPaths";
import { isMarionPartsModeEnabled } from "./marionPartsMode";

type Scene = components["schemas"]["Scene"];
type Character = components["schemas"]["Character"];
type VisualAssets = components["schemas"]["VisualAssets"];

const VISUAL_NOVEL_LAST_IMAGE_STORAGE_PREFIX = "palimia.visualNovelLastImageUrl:";
const DEFAULT_CHOICE_THINKING_ACTING_VARIANT_ID = "confronted";
function isReaderRefusal(error: unknown, fixed = false): error is ApiError {
  return error instanceof ApiError && (
    error.problem.type === "/errors/fixed-content-unavailable" ||
    error.problem.type === "/errors/fixed-asset-delivery-unavailable" ||
    error.problem.type === "/errors/invalid-reader-envelope" ||
    (error.status === 409 && error.problem.reason !== undefined && (fixed || isFixedReaderReason(error.problem.reason)))
  );
}

function mapReaderAccessRefusal(error: ApiError): ReturnType<typeof mapProblemDetail> {
  // 任意のserver detailを描画せず、statusの確定した意味だけを通知する。
  const mapped = mapProblemDetail(new ApiError(error.status, { ...error.problem, detail: "" }));
  const messages: Readonly<Record<number, { title: string; body: string }>> = {
    401: { title: "認証が必要です", body: "このプレイラインを継続するには認証が必要です。" },
    403: { title: "このプレイラインを利用できません", body: "このプレイラインの再生は許可されていません。" },
    404: { title: "このプレイラインは継続できません", body: "このプレイラインを利用できません。" },
  };
  return { ...mapped, ...messages[error.status] };
}

function getVisualNovelLastImageStorageKey(playlineId: string): string {
  return `${VISUAL_NOVEL_LAST_IMAGE_STORAGE_PREFIX}${playlineId}`;
}

function readVisualNovelLastImageUrl(playlineId: string): string | null {
  try {
    const value = window.localStorage.getItem(
      getVisualNovelLastImageStorageKey(playlineId),
    );
    return value !== null && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeVisualNovelLastImageUrl(
  playlineId: string,
  imageUrl: string | null,
): void {
  try {
    const key = getVisualNovelLastImageStorageKey(playlineId);
    if (imageUrl === null) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, imageUrl);
  } catch {
    // 読書ループを止めないため、永続化できない環境ではメモリ上のフォールバックだけ使う。
  }
}

function toVisualNovelMarionActor(
  character: Character | undefined,
  position: VisualNovelMarionActor["position"],
  actingVariantId?: string | undefined,
  isSpeaking?: boolean | undefined,
  visualAssets?: VisualAssets | undefined,
): VisualNovelMarionActor | null {
  if (character === undefined) return null;
  const basePortraitUrl = character.portraitUrl ?? null;
  const versioned = visualAssets?.mode === "versioned";
  const variantStandingUrl = resolveVariantStandingImageUrl(basePortraitUrl, actingVariantId, visualAssets, character.id);
  const portraitUrl = versioned ? variantStandingUrl : variantStandingUrl ?? basePortraitUrl;
  const scenarioKey = parseMarionStillPath(basePortraitUrl)?.scenarioKey ?? null;
  const partsModeEnabled = isMarionPartsModeEnabled(scenarioKey);
  const { manifestUrl, renderMode } = resolveMarionStandingRenderConfig(
    basePortraitUrl,
    partsModeEnabled,
    visualAssets,
  );
  if (manifestUrl === null && portraitUrl === null) return null;
  const normalizedVariantId = versioned ? actingVariantId ?? null : normalizeActingVariantId(actingVariantId);
  return {
    id: normalizedVariantId === null
      ? character.id
      : `${character.id}:${normalizedVariantId}`,
    characterId: character.id,
    ...(isSpeaking !== undefined ? { isSpeaking } : {}),
    name: character.name,
    manifestUrl,
    fallbackImageUrl: portraitUrl,
    variantId: normalizedVariantId,
    shot: "standing",
    renderMode,
    ...(versioned ? { fallbackImageBackupUrl: null, exactImageOnly: true } : variantStandingUrl !== null && basePortraitUrl !== null
      ? { fallbackImageBackupUrl: basePortraitUrl }
      : {}),
    position,
  };
}

function toVisualNovelCutinActor(
  character: Character | undefined,
  position: VisualNovelCutinActor["position"],
  actingVariantId?: string | undefined,
  approvedImageUrl?: string | undefined,
  visualAssets?: VisualAssets | undefined,
): VisualNovelCutinActor | null {
  if (character === undefined) return null;
  const basePortraitUrl = character.portraitUrl ?? null;
  const versioned = visualAssets?.mode === "versioned";
  const manifestUrl = !versioned && approvedImageUrl === undefined
    ? inferMarionManifestUrl(basePortraitUrl)
    : null;
  const cutinImageUrl = resolveCutinImageUrl(basePortraitUrl, actingVariantId, visualAssets, character.id);
  const variantStandingUrl = resolveVariantStandingImageUrl(basePortraitUrl, actingVariantId);
  const imageUrl = versioned ? cutinImageUrl : approvedImageUrl ?? cutinImageUrl ?? variantStandingUrl ?? basePortraitUrl;
  if (imageUrl === null) return null;
  const normalizedVariantId = versioned ? actingVariantId ?? null : normalizeActingVariantId(actingVariantId);
  return {
    id: normalizedVariantId === null
      ? `${character.id}:cutin`
      : `${character.id}:${normalizedVariantId}:cutin`,
    characterId: character.id,
    name: character.name,
    manifestUrl,
    imageUrl,
    fallbackImageBackupUrl: !versioned && approvedImageUrl === undefined
      ? variantStandingUrl ?? basePortraitUrl
      : null,
    variantId: normalizedVariantId,
    ...(versioned || approvedImageUrl !== undefined ? { exactImageOnly: true } : {}),
    position,
  };
}

function getDialogueSpeakerIds(segments: Scene["segments"]): string[] {
  const speakerIds: string[] = [];
  for (const segment of segments) {
    if (segment.type !== "dialogue") continue;
    if (speakerIds.includes(segment.speakerId)) continue;
    speakerIds.push(segment.speakerId);
  }
  return speakerIds;
}

function getVisualNovelActorPosition(
  index: number,
  actorCount: number,
  characterIndex: number,
): VisualNovelMarionActor["position"] {
  if (actorCount <= 1) {
    return characterIndex === 0 ? "left" : "right";
  }
  if (actorCount === 2) {
    return index === 0 ? "left" : "right";
  }
  if (index === 0) return "left";
  if (index === 1) return "center";
  return "right";
}

function resolveVisualNovelSceneActors(
  characters: Character[],
  segments: Scene["segments"],
  activeCue: VisualNovelActorCue | null,
  visualAssets?: VisualAssets | undefined,
): VisualNovelMarionActor[] {
  const speakerIds = getDialogueSpeakerIds(segments);
  if (
    activeCue !== null &&
    !speakerIds.includes(activeCue.speakerId)
  ) {
    speakerIds.push(activeCue.speakerId);
  }
  const orderedCharacters = characters.filter((character) =>
    speakerIds.includes(character.id),
  );
  if (orderedCharacters.length === 0) return [];
  const limitedCharacters = orderedCharacters.slice(0, 3);
  if (
    activeCue !== null &&
    !limitedCharacters.some((character) => character.id === activeCue.speakerId)
  ) {
    const activeCharacter = characters.find(
      (character) => character.id === activeCue.speakerId,
    );
    if (activeCharacter !== undefined) {
      limitedCharacters[limitedCharacters.length - 1] = activeCharacter;
    }
  }
  return limitedCharacters.flatMap((character, index) => {
    const characterIndex = characters.findIndex(
      (entry) => entry.id === character.id,
    );
    const actor = toVisualNovelMarionActor(
      character,
      getVisualNovelActorPosition(index, limitedCharacters.length, characterIndex),
      character.id === activeCue?.speakerId ? activeCue.actingVariantId : undefined,
      activeCue !== null ? character.id === activeCue.speakerId : undefined,
      visualAssets,
    );
    return actor === null ? [] : [actor];
  });
}

function toChoiceListChoice(choice: Scene["choices"][number]): ChoiceListChoice {
  return {
    id: choice.id,
    text: choice.text,
    ...(choice.emotion !== undefined ? { emotion: choice.emotion } : {}),
    ...(choice.requiresMemory !== undefined
      ? { requiresMemory: choice.requiresMemory }
      : {}),
    ...(choice.visualCue !== undefined ? { visualCue: choice.visualCue } : {}),
  };
}

function toProtagonistThinkingCue(
  cue: ChoiceVisualCue | null | undefined,
  protagonist: Character | undefined,
  options?: { fallbackActingVariantId?: string | undefined },
): VisualNovelActorCue | null {
  if (cue == null || protagonist === undefined) return null;
  const cueActingVariantId =
    cue.characterId === protagonist.id
      ? normalizeActingVariantId(cue.actingVariantId)
      : null;
  const fallbackActingVariantId = normalizeActingVariantId(
    options?.fallbackActingVariantId,
  );
  const actingVariantId =
    cueActingVariantId ?? fallbackActingVariantId ?? undefined;
  return {
    speakerId: protagonist.id,
    ...(actingVariantId !== undefined ? { actingVariantId } : {}),
  };
}

function toDefaultProtagonistThinkingCue(
  protagonist: Character | undefined,
): VisualNovelActorCue | null {
  if (protagonist === undefined) return null;
  return {
    speakerId: protagonist.id,
    actingVariantId: DEFAULT_CHOICE_THINKING_ACTING_VARIANT_ID,
  };
}

function toChoiceThinkingCutinCue(
  cue: ChoiceVisualCue | null,
  protagonist: Character | undefined,
): VisualNovelActorCue | null {
  if (protagonist !== undefined) {
    return {
      speakerId: protagonist.id,
      actingVariantId: DEFAULT_CHOICE_THINKING_ACTING_VARIANT_ID,
    };
  }
  if (cue === null) return null;
  const actingVariantId =
    normalizeActingVariantId(cue.actingVariantId) ??
    DEFAULT_CHOICE_THINKING_ACTING_VARIANT_ID;
  return {
    speakerId: cue.characterId,
    actingVariantId,
  };
}

/**
 * 画面状態マシン (design.md「画面状態マシン」) の唯一のホスト。
 * Idle / Starting / Playing / Ending / Error の各状態を
 * 子コンポーネントの表示切り替えで表現する。
 *
 * - Idle / Starting の導線は {@link ScenarioStart} に委譲する。
 *   ScenarioStart 内部で `useCreatePlayline` が走り、成功時に
 *   playlineStorage への保存まで完了する (2.4 の契約)。
 * - Playing / Ending は playlineId が確定している状態でのみ必要な
 *   `usePerformAction` を持つ {@link PlayingState} に分離する。
 *   これにより、Idle 状態でダミー playlineId を渡すハックを避ける。
 */
type PlayerScreenProps = {
  onPlayerBackgroundColorChange?: (scenarioId: string, color: string | null) => void;
};

export function PlayerScreen({
  onPlayerBackgroundColorChange,
}: PlayerScreenProps = {}): JSX.Element {
  const env = useMemo(() => loadClientEnv(), []);

  // 正規URLは /play/:scenarioId。既存導線の ?s= は読み取り互換として残す。
  const scenarioRoute = useMemo(() => {
    return resolveScenarioRouteFromLocation(window.location, env.demoScenarioId);
  }, [env.demoScenarioId]);
  const scenarioId = scenarioRoute.scenarioId;
  const requireScenarioMatch = scenarioRoute.source !== "fallback";

  useEffect(() => {
    if (scenarioRoute.source !== "query") return;
    window.history.replaceState(
      window.history.state,
      "",
      buildScenarioPlayPath(scenarioId),
    );
  }, [scenarioId, scenarioRoute.source]);

  const [playlineId, setPlaylineId] = useState<string | null>(() =>
    playlineStorage.getForScenario(scenarioId, {
      requireScenarioMatch,
    }),
  );

  const handleStarted = (id: string): void => {
    setPlaylineId(id);
  };

  const handleRestart = useCallback((): void => {
    playlineStorage.clear();
    setPlaylineId(null);
  }, []);

  // fork 成功時に playlineId を新しい worldlineId に切り替える。
  // playlineId === worldlineId（同一エンティティ）のため playlineStorage に保存できる。
  const handleForkSuccess = useCallback((newWorldlineId: string): void => {
    playlineStorage.set(newWorldlineId, scenarioId);
    setPlaylineId(newWorldlineId);
  }, [scenarioId]);

  // playline-persistence: cloud に保存された playline からの自動再開。
  // signed-in reader が別端末/再訪時に「続きから読む」を選ぶと cloud playline を resume する。
  const handleResume = useCallback((cloudPlaylineId: string, cloudScenarioId: string): void => {
    playlineStorage.set(cloudPlaylineId, cloudScenarioId);
    setPlaylineId(cloudPlaylineId);
  }, []);

  if (playlineId === null) {
    // anonymous / cloud 不在では AutoResumePrompt が即 ScenarioStart を表示 (= 既存挙動互換)。
    return (
      <div className="mx-auto max-w-2xl">
        <AutoResumePrompt
          baseUrl={env.apiBaseUrl}
          onResume={handleResume}
          scenarioId={scenarioId}
          requireScenarioMatch={requireScenarioMatch}
        >
          <ScenarioStart onStarted={handleStarted} scenarioId={scenarioId} />
        </AutoResumePrompt>
      </div>
    );
  }

  return (
    <PlayingState
      // key={playlineId}: fork / restart で playlineId が変わった際に
      // PlayingState 内部の全 local state (deductionAttempts / deductionError /
      // forcedBadEndingShown / palimpsestOpen 等) を確実に reset する。
      key={playlineId}
      playlineId={playlineId}
      scenarioId={scenarioId}
      onRestart={handleRestart}
      onForkSuccess={handleForkSuccess}
      debug={env.debug}
      maxScenes={env.maxScenes}
      streamingEnabled={env.streamingEnabled}
      worldlinePalimpsestEnabled={env.worldlinePalimpsestEnabled}
      interactiveDeductionEnabled={env.interactiveDeductionEnabled}
      apiBaseUrl={env.apiBaseUrl}
      onPlayerBackgroundColorChange={onPlayerBackgroundColorChange}
    />
  );
}

type PlayingStateProps = {
  playlineId: string;
  scenarioId: string;
  onRestart: () => void;
  /** fork 成功時に新しい worldlineId を渡すコールバック */
  onForkSuccess: (newWorldlineId: string) => void;
  debug: boolean;
  maxScenes: number;
  streamingEnabled: boolean;
  /** worldline-palimpsest (Phase 3-1, Task 6.1) palimpsest ボタン + モーダル feature flag */
  worldlinePalimpsestEnabled: boolean;
  /** interactive-deduction (Phase 3-3, Task 6.6) DeductionPanel feature flag */
  interactiveDeductionEnabled: boolean;
  /** mobile-pwa Phase 4.3: ReportModal の baseUrl (= 親 PlayerScreen の env.apiBaseUrl)。 */
  apiBaseUrl: string;
  onPlayerBackgroundColorChange?: ((scenarioId: string, color: string | null) => void) | undefined;
};

/**
 * playlineId が確定している状態で必要な hooks をまとめるサブコンポーネント。
 * `usePerformAction` は playlineId を必須引数に取るため、Idle 状態で
 * ダミー値を渡す代わりに、playlineId が非 null のときだけ本コンポーネントを
 * マウントする構造にしている。
 */
function PlayingState({
  playlineId,
  scenarioId,
  onRestart,
  onForkSuccess,
  debug,
  maxScenes,
  streamingEnabled,
  worldlinePalimpsestEnabled,
  interactiveDeductionEnabled,
  apiBaseUrl,
  onPlayerBackgroundColorChange,
}: PlayingStateProps): JSX.Element {
  const { selectedModel, availableModels, setModel } = useModelSelector();

  // 6 modal 開閉 state を useToggle primitive に集約 (= 各 useState(false) + open / close
  // closure ×2 = 計 18 行から 6 行に集約)。 各 modal の open/close handler を直接渡せる。
  const [backscrollOpen, openBackscroll, closeBackscroll] = useToggle();
  const [treeOpen, openTree, closeTree] = useToggle();
  // worldline-palimpsest (Phase 3-1, Task 6.1): palimpsest 2D view の開閉。
  // 開いている間は autoAdvance を一時停止し (StreamingSceneView.paused)、閉じたら元の state へ復帰。
  const [palimpsestOpen, openPalimpsest, closePalimpsest] = useToggle();
  // character-unlock (Task 4.1): キャラ図鑑モーダルの開閉。
  // CharacterCodexView 内部で fetch を持つため PlayerScreen は trigger と表示制御のみ担当。
  const [codexOpen, openCodex, closeCodex] = useToggle();
  // scene-summary-feed (Phase γ, Task 7.1): scenario 単位の cross-reader summary feed を
  // 覗く modal。 開いている間だけ SceneSummaryFeed を mount し、 閉じれば fetch も止まる
  // (= 既存 backscroll / palimpsest と同 doctrine)。
  const [summaryFeedOpen, openSummaryFeed, closeSummaryFeed] = useToggle();
  // scenario-spawn-mvp (Phase γ', Task 7.1): 「あなたの物語の派生」 modal。
  // 開いている間だけ ScenarioSpawnNotification を mount し、 閉じれば fetch も止まる
  // (= sibling SceneSummaryFeed と同 doctrine)。
  const [spawnNotificationOpen, openSpawnNotification, closeSpawnNotification] =
    useToggle();
  // mobile-pwa Phase 4.3: long-press で起動する通報 modal の open state。
  // ReportModal は targetType=scene + 現 scene.id を渡して mount される。
  const [reportModalOpen, openReportModal, closeReportModal] = useToggle();
  const [readerMenuOpen, setReaderMenuOpen] = useState(false);
  const [desktopMoreMenuOpen, setDesktopMoreMenuOpen] = useState(false);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  // toast 用に PlayerScreen 側でも useCharacterUnlock を呼ぶ。react-query は queryKey
  // (= ['character-unlock', playlineId]) で cache 共有するため CharacterCodexView 内の
  // 同 hook 呼出と二重 fetch にはならない (codex closed 時は codex 側が disabled、
  // PlayerScreen 側は常時有効で toast 用の昇格検知を担う)。
  const { promotedCharacters, dismissPromotion } =
    useCharacterUnlock(playlineId);
  // interactive-deduction (Phase 3-3, Task 6.6): DeductionPanel が active な間は
  // streaming scene の typewriter を一時停止する (palimpsest と同じ paused 連動)。
  const [deductionActive, setDeductionActive] = useState(false);
  // interactive-deduction (follow-up polish): HP を DeductionResponse.remainingAttempts で追跡。
  // GET /playlines/:id endpoint が未実装のため初期値は null、初回 submit 後に実値が populate される。
  const [deductionAttempts, setDeductionAttempts] = useState<number | null>(null);
  // 選択した choice をローディング中に表示するための state。
  // isWaitingForFirstSegment の間、StreamingSceneView に渡して chosen スタイルで1枚表示する。
  const [selectedChoice, setSelectedChoice] = useState<ChoiceListChoice | null>(null);
  const [pendingChoiceList, setPendingChoiceList] =
    useState<ChoiceListChoice[] | null>(null);
  const [pendingChoicePrompt, setPendingChoicePrompt] =
    useState<string | undefined>(undefined);
  // visual_novel mode の click/tap 送り。親レイアウトで受けた click を
  // SceneView / StreamingSceneView に単調増加 signal として渡す。
  const [visualNovelAdvanceSignal, setVisualNovelAdvanceSignal] = useState(0);
  const handleVisualNovelAdvanceText = useCallback((): void => {
    setVisualNovelAdvanceSignal((prev) => prev + 1);
  }, []);
  const [visualNovelLogOpenSignal, setVisualNovelLogOpenSignal] = useState(0);
  const handleVisualNovelOpenLog = useCallback((): void => {
    setVisualNovelLogOpenSignal((prev) => prev + 1);
  }, []);
  const [actorCueState, setActorCueState] =
    useState<{ sceneId: string | undefined; cue: VisualNovelActorCue | null }>({ sceneId: undefined, cue: null });
  const [visualNovelChoicePreview, setVisualNovelChoicePreview] =
    useState<ChoicePreview | null>(null);
  const [visualNovelChoiceThinkingCue, setVisualNovelChoiceThinkingCue] =
    useState<ChoiceVisualCue | null>(null);
  const [lastVisualNovelImageUrl, setLastVisualNovelImageUrl] =
    useState<string | null>(null);
  // forcedBadEnding 応答後に「palimpsest で別世界線へ」ボタンを表示するためのフラグ。
  // spec Req 4.4 "prominent button 表示" 準拠 (auto-open から差し替え)。
  const [forcedBadEndingShown, showForcedBadEnding, hideForcedBadEnding] =
    useToggle();
  // DeductionHost からのエラーを ErrorBanner に表示するための state。
  const [deductionError, setDeductionError] = useState<
    ReturnType<typeof mapProblemDetail> | null
  >(null);
  // シナリオのキャラクター一覧を取得して SceneView / StreamingSceneView へ渡す。
  // staleTime: Infinity のため初回のみフェッチし、以後はキャッシュを参照する。
  const scenarioQuery = useScenario(scenarioId);
  const currentScene = useCurrentScene(playlineId, scenarioQuery.data?.generationMode);
  const currentSceneData = currentScene.data;
  const hasCurrentActorCue = actorCueState.sceneId === currentSceneData?.id;
  const visualNovelActorCue = hasCurrentActorCue ? actorCueState.cue : null;
  const setVisualNovelActorCue = useCallback((cue: VisualNovelActorCue | null): void => {
    setActorCueState({ sceneId: currentSceneData?.id, cue });
  }, [currentSceneData?.id]);
  const fixedContext = currentSceneData?.fixedContext;
  const isFixedScene = fixedContext !== undefined || currentSceneData?.fixedContentVersionId != null || currentSceneData?.fixedContentNodeKey != null || scenarioQuery.data?.generationMode === "pre-baked";
  // overviewは最新公開版なので、pin版の不足した表示情報を補完しない。
  const displayScenario = fixedContext?.scenario ?? (currentSceneData !== undefined && !isFixedScene ? scenarioQuery.data : undefined);
  const introScenario = scenarioQuery.data?.generationMode === "pre-baked" ? displayScenario : scenarioQuery.data;
  const visualAssets = fixedContext?.visualAssets;
  const isVersioned = visualAssets?.mode === "versioned";
  const playScreenBackgroundColor = !isFixedScene && displayScenario !== undefined ? scenarioQuery.data?.playScreenBackgroundColor ?? null : null;
  const playPanelBackgroundColor = !isFixedScene && displayScenario !== undefined ? scenarioQuery.data?.playPanelBackgroundColor ?? null : null;
  const characters: Character[] = displayScenario?.characters ?? [];
  const performAction = usePerformAction(playlineId, selectedModel);
  const [streamState, streamActions] = useSceneStream(isFixedScene ? { generationMode: "pre-baked" } : undefined);
  const resetStream = streamActions.reset;
  const queryClient = useQueryClient();
  useEffect(() => {
    // React描画を待たずcurrent拒否を観測し、遅いcompleteによるquery上書きを止める。
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error"
        && event.query.queryKey.length === 2 && event.query.queryKey[0] === "currentScene" && event.query.queryKey[1] === playlineId
        && !canUseReaderCache(event.query.state.error)) {
        resetStream();
      }
    });
  }, [queryClient, playlineId, resetStream]);
  const currentRefused = currentScene.isError && !canUseReaderCache(currentScene.error);
  useEffect(() => {
    if (currentRefused) resetStream();
  }, [currentRefused, resetStream]);
  // playline-persistence: scene 進行ごとに cloud へ progress を sync (= 2026-06-02 採択)。
  // anonymous / network error は hook 内で silent skip、 409 で conflict modal を trigger。
  const playlineSync = usePlaylineSync(apiBaseUrl);

  // scene-background-library (機構 M15) Task 3.4:
  // visual_novel モード時のみ背景ライブラリを fetch する。
  // text_scroll モードでは enabled=false にして fetch を行わない (req 4.5)。
  // Hook は早期リターン前に呼ばなければならないため (= React Hook ルール)、
  // displayMode は scenarioQuery.data から直接算出する。
  const { getImageUrl } = useSceneBackground(
    scenarioId,
    currentSceneData !== undefined && (displayScenario?.displayMode ?? "text_scroll") === "visual_novel" && (!isFixedScene || fixedContext !== undefined),
    apiBaseUrl,
    visualAssets,
  );
  const playerBackgroundColor = currentSceneData?.isEnding
    ? displayScenario?.endingBackgroundColor ?? null
    : playScreenBackgroundColor;
  useEffect(() => {
    onPlayerBackgroundColorChange?.(scenarioId, playerBackgroundColor);
    return () => onPlayerBackgroundColorChange?.(scenarioId, null);
  }, [onPlayerBackgroundColorChange, playerBackgroundColor, scenarioId]);
  const scenarioDisplayMode = displayScenario?.displayMode;
  const displayMode = scenarioDisplayMode ?? "text_scroll";
  const isVisualNovelMode = displayMode === "visual_novel";
  const currentImageUrl = isVersioned
    ? getImageUrl(currentSceneData?.fixedContentNodeKey)
    : getImageUrl(currentSceneData?.backgroundLabel) ?? getImageUrl(currentSceneData?.fixedContentNodeKey);
  const visualNovelImageUrl = isVersioned ? currentImageUrl : currentImageUrl ?? lastVisualNovelImageUrl;

  // scene-beat-preview-toggle Phase 3: reader settings の `showBeatStructure` toggle 経由で
  // 通常 view にも CurrentBeatIndicator を inline mount するかを判定する。 default false で
  // 既存 reader UX 不変、 true で scene render 直前に inline indicator が表示される (= debug
  // overlay 経路と並列共存、 placement-agnostic UI doctrine 継承)。
  const { preset: readerStylePreset } = useReaderStylePresetContext();

  // デバッグ用トラッキング: sceneNumber / genTimes / tokens / model を localStorage に永続化。
  const [debugState, setDebugState] = usePlaylineDebugState(playlineId);
  const { sceneNumber, genTimes, tokens: totalTokens, model } = debugState;
  const pendingStartRef = useRef<number | null>(null);

  const toggleReaderMenu = useCallback((): void => {
    setReaderMenuOpen((open) => !open);
  }, []);

  const handleReaderMenuAction = useCallback((action: () => void): void => {
    setReaderMenuOpen(false);
    action();
  }, []);

  const toggleDesktopMoreMenu = useCallback((): void => {
    setDesktopMoreMenuOpen((open) => !open);
  }, []);

  const toggleDebugPanel = useCallback((): void => {
    setDebugPanelOpen((open) => !open);
  }, []);

  const handleDesktopMoreMenuAction = useCallback((action: () => void): void => {
    setDesktopMoreMenuOpen(false);
    action();
  }, []);

  // プロンプトサマリー（要件 4.3）
  const [lastPromptSummary, setLastPromptSummary] = useState<string | null>(null);

  // エラー履歴（要件 4.4）: 最大 3 件、新着を先頭に追加し末尾を捨てる
  const [recentErrors, setRecentErrors] = useState<string[]>([]);

  useEffect(() => {
    return onTokenUsage((usage) => {
      setDebugState((prev) => ({
        ...prev,
        tokens: {
          input: prev.tokens.input + usage.inputTokens,
          output: prev.tokens.output + usage.outputTokens,
        },
        model: usage.model,
      }));
    });
  }, [setDebugState]);

  // プロンプトサマリーの購読（要件 4.3）
  // onPromptSummary は登録解除関数を返すため、そのまま cleanup として使う。
  useEffect(() => {
    return onPromptSummary((summary) => {
      setLastPromptSummary(summary);
    });
  }, []);

  useEffect(() => {
    if (performAction.isPending) {
      pendingStartRef.current = Date.now();
    } else if (pendingStartRef.current !== null) {
      const elapsed = Date.now() - pendingStartRef.current;
      pendingStartRef.current = null;
      if (performAction.isSuccess) {
        setDebugState((prev) => ({
          ...prev,
          sceneNumber: prev.sceneNumber + 1,
          genTimes: [...prev.genTimes, elapsed],
        }));
        // playline-persistence: scene 進行成功で cloud へ progress を fire-and-forget sync。
        playlineSync.syncNow(playlineId);
      }
    }
  }, [performAction.isPending, performAction.isSuccess, setDebugState, playlineSync, playlineId]);

  // ストリーミング完了を検知してシーン番号を更新し、currentScene キャッシュを更新する（要件 4.1, 4.5）。
  // complete イベント受信後（isStreaming=false かつ choices が来た）に処理する。
  // Scene付きcompleteはhookが同じqueryへ保存済み。再取得で確定envelopeを上書きしない。
  // Sceneを持たないruntime completeだけ従来どおり再取得する。
  const prevIsStreamingRef = useRef<boolean>(false);
  useEffect(() => {
    const justCompleted =
      streamingEnabled &&
      prevIsStreamingRef.current &&
      !streamState.isStreaming &&
      streamState.choices !== null;
    prevIsStreamingRef.current = streamState.isStreaming;

    if (justCompleted) {
      const metrics = streamState.streamingMetrics;
      setDebugState((prev) => ({
        ...prev,
        sceneNumber: prev.sceneNumber + 1,
        ...(metrics?.model !== undefined && { model: metrics.model }),
        ...(metrics?.inputTokens !== undefined || metrics?.outputTokens !== undefined
          ? {
              tokens: {
                input: prev.tokens.input + (metrics.inputTokens ?? 0),
                output: prev.tokens.output + (metrics.outputTokens ?? 0),
              },
            }
          : {}),
        ...(metrics?.latencyMs !== undefined
          ? { genTimes: [...prev.genTimes, metrics.latencyMs] }
          : {}),
      }));
      if (metrics?.promptSummary) {
        setLastPromptSummary(metrics.promptSummary);
      }
      // currentScene キャッシュを無効化して最新シーンを取得し、次回アクションで
      // 正しい sceneId を参照できるようにする。
      if (streamState.scene === undefined) {
        void queryClient.invalidateQueries({ queryKey: ["currentScene", playlineId] });
      }
      // playline-persistence: streaming 完了でも cloud へ progress を sync。
      playlineSync.syncNow(playlineId);
    }
  }, [streamState.isStreaming, streamState.choices, streamState.streamingMetrics, streamState.scene, streamingEnabled, playlineId, queryClient, setDebugState, setLastPromptSummary, playlineSync]);

  // エラー蓄積（要件 4.4）: performAction / currentScene / streamState のエラーを監視し、
  // 発生時に先頭へ追加して最大 3 件に制限する。
  useEffect(() => {
    const err = performAction.error ?? currentScene.error;
    if (err == null) return;
    const message = err instanceof Error ? err.message : String(err);
    setRecentErrors((prev) => [message, ...prev].slice(0, 3));
  }, [performAction.error, currentScene.error]);

  useEffect(() => {
    if (streamState.error == null) return;
    setRecentErrors((prev) => [streamState.error as string, ...prev].slice(0, 3));
  }, [streamState.error]);

  useEffect(() => {
    setVisualNovelChoicePreview(null);
    setVisualNovelChoiceThinkingCue(null);
    // versionedでは子viewの同版cue通知を親effectで上書きしない。
    if (!isVersioned) setVisualNovelActorCue(null);
    setSelectedChoice(null);
    setPendingChoiceList(null);
    setPendingChoicePrompt(undefined);
  }, [currentScene.data?.id, isVersioned, setVisualNovelActorCue]);

  useEffect(() => {
    if (scenarioDisplayMode !== "visual_novel") {
      setVisualNovelChoicePreview(null);
      setVisualNovelChoiceThinkingCue(null);
      setVisualNovelActorCue(null);
      setPendingChoiceList(null);
      setPendingChoicePrompt(undefined);
    }
  }, [scenarioDisplayMode, setVisualNovelActorCue]);

  useEffect(() => {
    if (scenarioDisplayMode === undefined || isVersioned) return;
    if (!isVisualNovelMode) {
      setLastVisualNovelImageUrl(null);
      writeVisualNovelLastImageUrl(playlineId, null);
      return;
    }
    if (currentImageUrl !== null) {
      setLastVisualNovelImageUrl(currentImageUrl);
      writeVisualNovelLastImageUrl(playlineId, currentImageUrl);
    } else {
      setLastVisualNovelImageUrl(readVisualNovelLastImageUrl(playlineId));
    }
  }, [currentImageUrl, isVisualNovelMode, isVersioned, playlineId, scenarioDisplayMode]);

  // 初回 fetch 中、または playlineId 復元直後の idle→fetching 中は
  // オーバーレイを出す。data が未到達の段階で SceneView に流すと
  // null アクセスで落ちるため、ロード完了を待ってから描画分岐する。
  // scenario-start-ux (Phase 4): 初回専用は ScenarioIntroOverlay に置換 (flag off で
  // 旧 LoadingOverlay に delegate される)。
  const retryReaderScene = async (): Promise<void> => {
    // 同じpinの再取得が成功するまでは拒否状態を外さない。
    const refreshed = await currentScene.refetch();
    if (refreshed.isSuccess) {
      performAction.reset();
      streamActions.reset();
    }
  };
  if (currentScene.isPending) {
    return (
      <ScenarioIntroOverlay
        isPending={true}
        phase="fetching-scene"
        scenario={introScenario}
        scenarioErrored={!isFixedScene && scenarioQuery.isError}
      />
    );
  }

  if (currentScene.isError) {
    const failure: unknown = currentScene.error;
    if (isReaderRefusal(failure, isFixedScene)) {
      return (
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
          <ReaderGenerationMode generationMode="pre-baked" />
          <FixedContentUnavailable reason={failure.problem.reason} onRetry={() => void retryReaderScene()} />
        </div>
      );
    }
    const uiError = currentScene.error instanceof ApiError && currentScene.error.status >= 400 && currentScene.error.status < 500
      ? mapReaderAccessRefusal(currentScene.error)
      : mapProblemDetail(currentScene.error);
    // exactOptionalPropertyTypes を有効にしているため、未使用ハンドラは
    // undefined 代入ではなく「プロパティ自体を渡さない」形で分岐する。
    const retryProps = uiError.retryable
      ? { onRetry: () => void currentScene.refetch() }
      : {};
    const restartProps = uiError.requiresRestart
      ? { onRestart }
      : {};
    return <ErrorBanner error={uiError} {...retryProps} {...restartProps} />;
  }

  const scene: Scene | undefined = currentSceneData;
  if (scene === undefined) {
    // 型ガード: isPending/isError を抜けた時点で data は定義されているはずだが、
    // react-query の型上 undefined の可能性が残るため明示フォールバックする。
    // scenario-start-ux: ここも初回専用 overlay に差し替え。
    return (
      <ScenarioIntroOverlay
        isPending={true}
        phase="fetching-scene"
        scenario={introScenario}
        scenarioErrored={!isFixedScene && scenarioQuery.isError}
      />
    );
  }

  const readerRefusal = isReaderRefusal(performAction.error, isFixedScene)
    ? performAction.error.problem
    : streamState.problem !== undefined && isReaderRefusal(new ApiError(streamState.problem.status, streamState.problem), isFixedScene)
      ? streamState.problem
      : undefined;
  const isFixedActionUnavailable = readerRefusal !== undefined || streamState.fixedContentUnavailable;
  if (isFixedActionUnavailable) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        <ReaderGenerationMode generationMode="pre-baked" />
        <FixedContentUnavailable reason={readerRefusal?.reason} onRetry={() => void retryReaderScene()} />
      </div>
    );
  }

  // 明示HTTP拒否では成功cacheを描画しない。従来Scene競合だけは再取得へ委ねる。
  const actionAccessError = performAction.error instanceof ApiError ? performAction.error : undefined;
  const streamAccessError = streamState.problem !== undefined
    ? new ApiError(streamState.problem.status, streamState.problem)
    : undefined;
  const accessRefusal = [actionAccessError, streamAccessError].find((error) => error !== undefined
    && error.status >= 400 && error.status < 500
    && !(error.status === 409 && error.problem.type === "/errors/conflict" && error.problem.reason === undefined)
    && !(!isFixedScene && error.status === 422 && error.problem.type === "/errors/invalid-action" && error.problem.reason === undefined));
  if (accessRefusal !== undefined) {
    return <ErrorBanner error={mapReaderAccessRefusal(accessRefusal)} />;
  }

  if (scene.isEnding) {
    return (
      <EndingView
        scene={scene}
        onRestart={onRestart}
        characters={characters}
        visualAssets={visualAssets}
        endingBackgroundColor={displayScenario?.endingBackgroundColor}
        endingTextColor={displayScenario?.endingTextColor}
      />
    );
  }

  // Playing 状態。performAction のエラーは 409 のみ「裏で invalidate 済み」
  // なのでバナー非表示、それ以外は UIError として描画する。
  // network 断 (TypeError) など ApiError 以外の例外もバナーで通知する必要が
  // あるため、「truthy なエラー」を起点に「409 ApiError のみ抑制」する条件へ
  // 反転させている (Req 6.4: ネットワーク断を沈黙させない)。
  const performError = performAction.error;
  const isSilenced409 =
    performError instanceof ApiError && performError.status === 409 && performError.problem.type === "/errors/conflict" && performError.problem.reason === undefined;
  const showBanner = performError != null && !isSilenced409;
  // deductionError が set されている場合は優先表示 (駆け引き固有の文言を隠さない)。
  const uiError =
    deductionError !== null
      ? deductionError
      : showBanner
        ? mapProblemDetail(performError)
        : streamState.error !== null
          ? mapProblemDetail(streamAccessError !== undefined
            ? new ApiError(streamAccessError.status, { ...streamAccessError.problem, detail: "" })
            : new TypeError("Stream connection failed"))
          : undefined;

  const lastGen = genTimes.length > 0 ? (genTimes[genTimes.length - 1] ?? null) : null;
  const avgGen = genTimes.length > 0 ? genTimes.reduce((a, b) => a + b, 0) / genTimes.length : null;
  const remaining = maxScenes - sceneNumber;
  const eta = avgGen !== null && remaining > 0 ? avgGen * remaining : null;
  const rememberPendingChoiceList = (
    choices: Scene["choices"],
    choiceId: string,
    segments: Scene["segments"],
  ): void => {
    const list = choices.map(toChoiceListChoice);
    setPendingChoiceList(list);
    setSelectedChoice(list.find((choice) => choice.id === choiceId) ?? null);
    setPendingChoicePrompt(
      isVisualNovelMode && list.length > 0
        ? resolveChoicePrompt({
            segments,
            choices: list,
            protagonist: characters[0],
          })
        : undefined,
    );
  };

  // hookが確定envelopeを保存する同じqueryから、次の操作のScene IDを取得する。
  const handleStreamChoice = (choiceId: string): void => {
    const sceneId = currentScene.data?.id ?? scene.id;
    rememberPendingChoiceList(scene.choices, choiceId, scene.segments);
    setVisualNovelChoicePreview(null);
    streamActions.startStream(playlineId, { type: "choice", choiceId, sceneId });
  };
  const handleStreamAdvance = (): void => {
    const sceneId = currentScene.data?.id ?? scene.id;
    setVisualNovelChoicePreview(null);
    streamActions.startStream(playlineId, { type: "advance", sceneId });
  };

  // 固定版complete後はcurrent queryのScene/node/contextを正本にする。
  // runtimeのSceneなしcompleteは従来のstream読了表示を維持する。
  const isStreamingActive = streamingEnabled && (streamState.isStreaming ||
    (streamState.segments.length > 0 && streamState.scene === undefined));
  const protagonistCharacter = characters[0];
  const selectedVisualCue = selectedChoice?.visualCue;
  const isVisualNovelChoiceGenerationPending =
    isVisualNovelMode &&
    selectedChoice !== null &&
    (performAction.isPending || streamState.isStreaming);
  const selectedCueActive =
    selectedChoice !== null &&
    !isVisualNovelChoiceGenerationPending &&
    (isStreamingActive || performAction.isPending);
  const selectedLayer =
    selectedVisualCue?.confirmLayer ?? selectedVisualCue?.previewLayer ?? "standing";
  const isVisualNovelComposing =
    isVisualNovelMode && streamState.isStreaming && streamState.segments.length === 0;
  const visualNovelComposingPreview: ChoicePreview | null =
    isVisualNovelComposing && selectedChoice !== null
      ? {
          id: selectedChoice.id,
          text: stripInlineMarkup(selectedChoice.text),
          ...(selectedChoice.emotion !== undefined && selectedChoice.emotion.trim().length > 0
            ? { emotion: stripInlineMarkup(selectedChoice.emotion) }
            : {}),
          ...(selectedChoice.visualCue !== undefined
            ? { visualCue: selectedChoice.visualCue }
            : {}),
        }
      : null;
  const selectedActorCue: VisualNovelActorCue | null =
    selectedCueActive &&
    selectedVisualCue !== undefined &&
    selectedLayer === "standing"
      ? {
          speakerId: selectedVisualCue.characterId,
          actingVariantId: selectedVisualCue.actingVariantId,
        }
      : null;
  const selectedCutinCue: VisualNovelActorCue | null =
    selectedCueActive &&
    selectedVisualCue !== undefined &&
    selectedLayer === "cutin"
      ? {
          speakerId: selectedVisualCue.characterId,
          actingVariantId: selectedVisualCue.actingVariantId,
        }
      : null;
  const isChoiceDecisionActive =
    visualNovelChoiceThinkingCue !== null || visualNovelChoicePreview !== null;
  const generationChoiceThinkingCue =
    isVisualNovelChoiceGenerationPending
      ? visualNovelChoiceThinkingCue ??
        (isVersioned
          ? (pendingChoiceList ?? []).find((choice) => choice.visualCue !== undefined)?.visualCue ?? null
          : resolveChoiceThinkingCue(pendingChoiceList ?? [], protagonistCharacter?.id))
      : null;
  const decisionVisualCue = isVisualNovelChoiceGenerationPending
    ? generationChoiceThinkingCue
    : visualNovelChoicePreview?.visualCue ?? visualNovelChoiceThinkingCue;
  const decisionLayer = isVisualNovelChoiceGenerationPending
    ? decisionVisualCue?.confirmLayer ?? decisionVisualCue?.previewLayer ?? "standing"
    : decisionVisualCue?.previewLayer ?? "standing";
  // 初回の明示dialogueはeffect通知を待たず解決し、default画像の一瞬の混在も防ぐ。
  const firstActorSegment = (isStreamingActive ? streamState.segments : scene.segments)[0];
  const currentGraphActorCue = hasCurrentActorCue ? visualNovelActorCue : (isVersioned && firstActorSegment?.type === "dialogue"
    ? {
        speakerId: firstActorSegment.speakerId,
        ...(firstActorSegment.actingVariantId !== undefined ? { actingVariantId: firstActorSegment.actingVariantId } : {}),
        ...(firstActorSegment.cutin === true ? { cutin: true as const } : {}),
      }
    : null);
  const approvedDecisionCue: VisualNovelActorCue | null = decisionVisualCue !== null
    ? { speakerId: decisionVisualCue.characterId, actingVariantId: decisionVisualCue.actingVariantId }
    : null;
  const choiceThinkingCutinCue: VisualNovelActorCue | null =
    !selectedCueActive && (isChoiceDecisionActive || isVisualNovelChoiceGenerationPending)
      ? isVersioned
        ? decisionLayer === "cutin" ? approvedDecisionCue : null
        : toChoiceThinkingCutinCue(
          isVisualNovelChoiceGenerationPending
            ? generationChoiceThinkingCue
            : visualNovelChoiceThinkingCue,
          protagonistCharacter,
        )
      : null;
  const activeVisualNovelActorCue =
    (isChoiceDecisionActive || isVisualNovelChoiceGenerationPending) && !selectedCueActive
      ? isVersioned && decisionLayer === "standing"
        ? approvedDecisionCue
        : null
      : selectedActorCue ?? currentGraphActorCue;
  const dialogueCutinCue =
    !isChoiceDecisionActive && !isVisualNovelChoiceGenerationPending &&
    currentGraphActorCue?.cutin === true && (isVersioned || currentGraphActorCue.cutinImageUrl !== undefined)
      ? currentGraphActorCue
      : null;
  const activeVisualNovelCutinCue =
    selectedCutinCue ?? choiceThinkingCutinCue ?? dialogueCutinCue;
  const currentCutinCharacterIndex = characters.findIndex(
    (character) => character.id === activeVisualNovelCutinCue?.speakerId,
  );
  const currentCutinActor = isVisualNovelMode
    ? toVisualNovelCutinActor(
        currentCutinCharacterIndex >= 0
          ? characters[currentCutinCharacterIndex]
          : undefined,
        currentCutinCharacterIndex === 0 ? "left" : "right",
        activeVisualNovelCutinCue?.actingVariantId,
        activeVisualNovelCutinCue?.cutinImageUrl,
        visualAssets,
      )
    : null;
  const visualNovelActorSourceSegments = isStreamingActive
    ? streamState.segments
    : scene.segments;
  const currentMarionActors =
    isVisualNovelMode
      ? resolveVisualNovelSceneActors(
          characters,
          visualNovelActorSourceSegments,
          activeVisualNovelActorCue,
          visualAssets,
        )
      : [];
  const choicePanelPosition =
    isVisualNovelMode &&
    (isChoiceDecisionActive || isVisualNovelChoiceGenerationPending)
      ? currentCutinActor?.position === "right"
        ? "left"
        : "right"
      : undefined;
  const renderDebugPanel = (): JSX.Element => (
    <DebugOverlay
      playlineId={playlineId}
      sceneNumber={sceneNumber}
      maxScenes={maxScenes}
      lastGenerationMs={lastGen}
      avgGenerationMs={avgGen}
      estimatedRemainingMs={eta}
      totalTokenInput={totalTokens.input}
      totalTokenOutput={totalTokens.output}
      model={model}
      onClearProgress={onRestart}
      selectedModel={selectedModel}
      availableModels={availableModels}
      onModelChange={setModel}
      genTimes={genTimes}
      recentErrors={recentErrors}
      lastPromptSummary={lastPromptSummary}
    />
  );
  const menuItemClass =
    "min-h-10 rounded border border-reader-border-strong bg-reader-page px-3 py-2 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring";
  const renderVisualNovelHudMenu = (): JSX.Element => (
    <div data-testid="visual-novel-hud-menu" className="flex flex-col items-end gap-2">
      <button
        type="button"
        data-testid="visual-novel-hud-menu-trigger"
        aria-label="visualモードメニューを開く"
        aria-expanded={readerMenuOpen}
        aria-controls="visual-novel-hud-menu-panel"
        onClick={toggleReaderMenu}
        className="flex size-11 items-center justify-center rounded-full border border-white/25 bg-slate-950/78 text-lg text-slate-50 shadow-[0_10px_26px_rgba(0,0,0,0.34)] backdrop-blur-md hover:bg-slate-900/88 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
      >
        ☰
      </button>
      {readerMenuOpen && (
        <div
          id="visual-novel-hud-menu-panel"
          data-testid="visual-novel-hud-menu-panel"
          className="grid max-h-[calc(100dvh-7rem)] w-64 max-w-[calc(100vw-2.5rem)] gap-2 overflow-y-auto overscroll-contain rounded-lg border border-white/20 bg-slate-950/90 p-2 text-slate-50 shadow-[0_18px_48px_rgba(0,0,0,0.45)] backdrop-blur-md"
        >
          <button
            type="button"
            onClick={() => { handleReaderMenuAction(openBackscroll); }}
            className={menuItemClass}
          >
            バックスクロール
          </button>
          <button
            type="button"
            onClick={() => { handleReaderMenuAction(openTree); }}
            className={menuItemClass}
          >
            世界線ツリー
          </button>
          {worldlinePalimpsestEnabled && (
            <button
              type="button"
              data-testid="visual-novel-hud-palimpsest-button"
              onClick={() => { handleReaderMenuAction(openPalimpsest); }}
              className="min-h-10 rounded border border-reader-brand bg-reader-page px-3 py-2 text-left text-sm text-reader-brand hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            >
              palimpsest
            </button>
          )}
          <button
            type="button"
            data-testid="visual-novel-hud-log-button"
            onClick={() => { handleReaderMenuAction(handleVisualNovelOpenLog); }}
            className={menuItemClass}
          >
            ログ
          </button>
          <button
            type="button"
            data-testid="visual-novel-hud-character-codex-trigger"
            onClick={() => { handleReaderMenuAction(openCodex); }}
            className={menuItemClass}
          >
            キャラ図鑑
          </button>
          <button
            type="button"
            data-testid="visual-novel-hud-scene-summary-feed-trigger"
            onClick={() => { handleReaderMenuAction(openSummaryFeed); }}
            className={menuItemClass}
          >
            世界線の足跡
          </button>
          <button
            type="button"
            data-testid="visual-novel-hud-scenario-spawn-notification-trigger"
            onClick={() => { handleReaderMenuAction(openSpawnNotification); }}
            className={menuItemClass}
          >
            あなたの物語の派生
          </button>
          {debug && (
            <>
              <button
                type="button"
                data-testid="visual-novel-hud-debug-panel-trigger"
                aria-expanded={debugPanelOpen}
                aria-controls="debug-overlay"
                onClick={toggleDebugPanel}
                className="min-h-10 rounded border border-reader-debug-border bg-reader-page px-3 py-2 text-left text-sm font-semibold text-reader-debug-fg-strong hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
              >
                DEBUG
              </button>
              {debugPanelOpen && (
                <div>
                  {renderDebugPanel()}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={
        isVisualNovelMode
          ? "relative left-1/2 flex w-[calc(100vw-2rem)] max-w-[1600px] -translate-x-1/2 flex-col gap-4"
          : "mx-auto flex w-full max-w-2xl flex-col gap-4"
      }
    >
      {!isVisualNovelMode && (
        <>
          {/* 世界線 UI エントリポイント（要件 2.1, 4.1, 5.1） */}
          <div className="flex flex-col items-end gap-2 md:hidden">
        <button
          type="button"
          data-testid="reader-mobile-menu-trigger"
          aria-expanded={readerMenuOpen}
          aria-controls="reader-mobile-menu-panel"
          onClick={toggleReaderMenu}
          className="mr-14 min-h-11 w-fit rounded border border-reader-border-strong bg-reader-surface px-3 py-1 text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
        >
          メニュー
        </button>
        {readerMenuOpen && (
          <div
            id="reader-mobile-menu-panel"
            data-testid="reader-mobile-menu-panel"
            className="grid w-full grid-cols-2 gap-2 rounded border border-reader-border bg-reader-surface p-2"
          >
            <button
              type="button"
              onClick={() => { handleReaderMenuAction(openBackscroll); }}
              className="min-h-10 rounded border border-reader-border-strong bg-reader-page px-2 py-1.5 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            >
              バックスクロール
            </button>
            <button
              type="button"
              onClick={() => { handleReaderMenuAction(openTree); }}
              className="min-h-10 rounded border border-reader-border-strong bg-reader-page px-2 py-1.5 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            >
              世界線ツリー
            </button>
            {worldlinePalimpsestEnabled && (
              <button
                type="button"
                data-testid="mobile-open-palimpsest-button"
                onClick={() => { handleReaderMenuAction(openPalimpsest); }}
                className="min-h-10 rounded border border-reader-brand bg-reader-page px-2 py-1.5 text-left text-sm text-reader-brand hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
              >
                palimpsest
              </button>
            )}
            {isVisualNovelMode && (
              <button
                type="button"
                data-testid="mobile-visual-novel-log-button"
                onClick={() => { handleReaderMenuAction(handleVisualNovelOpenLog); }}
                className="min-h-10 rounded border border-reader-border-strong bg-reader-page px-2 py-1.5 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
              >
                ログ
              </button>
            )}
            <button
              type="button"
              data-testid="mobile-character-codex-trigger"
              onClick={() => { handleReaderMenuAction(openCodex); }}
              className="min-h-10 rounded border border-reader-border-strong bg-reader-page px-2 py-1.5 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            >
              キャラ図鑑
            </button>
            <button
              type="button"
              data-testid="mobile-scene-summary-feed-trigger"
              onClick={() => { handleReaderMenuAction(openSummaryFeed); }}
              className="min-h-10 rounded border border-reader-border-strong bg-reader-page px-2 py-1.5 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            >
              世界線の足跡
            </button>
            <button
              type="button"
              data-testid="mobile-scenario-spawn-notification-trigger"
              onClick={() => { handleReaderMenuAction(openSpawnNotification); }}
              className="col-span-2 min-h-10 rounded border border-reader-border-strong bg-reader-page px-2 py-1.5 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            >
              あなたの物語の派生
            </button>
            {debug && (
              <>
                <button
                  type="button"
                  data-testid="mobile-debug-panel-trigger"
                  aria-expanded={debugPanelOpen}
                  aria-controls="debug-overlay"
                  onClick={toggleDebugPanel}
                  className="col-span-2 min-h-10 rounded border border-reader-debug-border bg-reader-page px-2 py-1.5 text-left text-sm font-semibold text-reader-debug-fg-strong hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
                >
                  DEBUG
                </button>
                {debugPanelOpen && (
                  <div className="col-span-2">
                    {renderDebugPanel()}
                  </div>
                )}
              </>
            )}
          </div>
        )}
          </div>
          <div data-testid="reader-desktop-menu" className="hidden w-full items-start gap-2 md:flex">
        <button
          type="button"
          onClick={openBackscroll}
          className="min-h-11 shrink-0 whitespace-nowrap px-3 py-1 text-sm bg-reader-surface hover:bg-reader-border text-reader-secondary rounded border border-reader-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
        >
          バックスクロール
        </button>
        <button
          type="button"
          onClick={openTree}
          className="min-h-11 shrink-0 whitespace-nowrap px-3 py-1 text-sm bg-reader-surface hover:bg-reader-border text-reader-secondary rounded border border-reader-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
        >
          世界線ツリー
        </button>
        {/* worldline-palimpsest (Phase 3-1): feature flag off 時はボタン非表示 */}
        {worldlinePalimpsestEnabled && (
          <button
            type="button"
            data-testid="open-palimpsest-button"
            onClick={openPalimpsest}
            className="min-h-11 shrink-0 whitespace-nowrap px-3 py-1 text-sm bg-reader-surface hover:bg-reader-border text-reader-brand rounded border border-reader-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
          >
            palimpsest
          </button>
        )}
        {isVisualNovelMode && (
          <button
            type="button"
            data-testid="desktop-visual-novel-log-button"
            onClick={handleVisualNovelOpenLog}
            className="min-h-11 shrink-0 whitespace-nowrap px-3 py-1 text-sm bg-reader-surface hover:bg-reader-border text-reader-secondary rounded border border-reader-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
          >
            ログ
          </button>
        )}
        {debug && (
          <div className="relative shrink-0">
            <button
              type="button"
              data-testid="desktop-debug-panel-trigger"
              aria-expanded={debugPanelOpen}
              aria-controls="debug-overlay"
              onClick={toggleDebugPanel}
              className="min-h-11 shrink-0 whitespace-nowrap rounded border border-reader-debug-border bg-reader-surface px-3 py-1 text-sm font-semibold text-reader-debug-fg-strong hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            >
              DEBUG
            </button>
            {debugPanelOpen && (
              <div className="absolute right-0 top-full z-30 mt-2">
                {renderDebugPanel()}
              </div>
            )}
          </div>
        )}
        <div className="relative shrink-0">
          <button
            type="button"
            data-testid="desktop-reader-more-menu-trigger"
            aria-label="その他の読書メニューを開く"
            aria-expanded={desktopMoreMenuOpen}
            aria-controls="desktop-reader-more-menu-panel"
            onClick={toggleDesktopMoreMenu}
            className="flex size-11 items-center justify-center rounded border border-reader-border-strong bg-reader-surface text-lg text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
          >
            ☰
          </button>
          {desktopMoreMenuOpen && (
            <div
              id="desktop-reader-more-menu-panel"
              data-testid="desktop-reader-more-menu-panel"
              className="absolute right-0 top-full z-30 mt-2 grid w-64 gap-2 rounded border border-reader-border bg-reader-surface p-2 shadow-lg"
            >
              {/* character-unlock (Task 4.1): キャラ図鑑 trigger。補助操作は desktop overflow 防止のため more menu に集約。 */}
              <button
                type="button"
                data-testid="character-codex-trigger"
                onClick={() => { handleDesktopMoreMenuAction(openCodex); }}
                className="min-h-10 rounded border border-reader-border-strong bg-reader-page px-3 py-2 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
              >
                キャラ図鑑
              </button>
              {/* scene-summary-feed (Phase γ, Task 7.1): scenario detail に summary feed 「タブ」 を additive。 */}
              <button
                type="button"
                data-testid="scene-summary-feed-trigger"
                onClick={() => { handleDesktopMoreMenuAction(openSummaryFeed); }}
                className="min-h-10 rounded border border-reader-border-strong bg-reader-page px-3 py-2 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
              >
                世界線の足跡
              </button>
              {/* scenario-spawn-mvp (Phase γ', Task 7.1): 当 scenario の派生情報を manual fetch + 表示する trigger。 */}
              <button
                type="button"
                data-testid="scenario-spawn-notification-trigger"
                onClick={() => { handleDesktopMoreMenuAction(openSpawnNotification); }}
                className="min-h-10 rounded border border-reader-border-strong bg-reader-page px-3 py-2 text-left text-sm text-reader-secondary hover:bg-reader-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
              >
                あなたの物語の派生
              </button>
            </div>
          )}
        </div>
          </div>
        </>
      )}

      {/* scene-beat-preview-toggle Phase 3: reader settings の showBeatStructure ON 時のみ、
          scene render 直前に inline CurrentBeatIndicator を mount。 Beat 未設定 scenario では
          内部で null render し UI 自体を消す silent fallback (= reader UX を破壊しない)。
          debug overlay 経路の fixed 配置版 (= top-left overlay) と並列共存、 こちらは inline。 */}
      {readerStylePreset.showBeatStructure && (
        <div data-testid="reader-beat-indicator" className="mb-2">
          <CurrentBeatIndicator
            baseUrl={apiBaseUrl}
            scenarioId={scenarioId}
            currentSceneId={currentScene.data?.id ?? scene.id}
          />
        </div>
      )}

      <ReaderGenerationMode generationMode={displayScenario?.generationMode} />

      {(() => {
        const sceneContent = isStreamingActive ? (
          <StreamingSceneView
            segments={streamState.segments}
            choices={streamState.choices}
            isEnding={streamState.isEnding}
            isStreaming={streamState.isStreaming}
            isPending={streamState.isStreaming}
            characters={characters}
            visualAssets={visualAssets}
            onSelectChoice={handleStreamChoice}
            onAdvance={handleStreamAdvance}
            paused={palimpsestOpen || deductionActive}
            selectedChoice={selectedChoice ?? undefined}
            manualReveal={isVisualNovelMode}
            advanceSignal={isVisualNovelMode ? visualNovelAdvanceSignal : undefined}
            clickAdvanceEnabled={!isVisualNovelMode}
            segmentVariant={isVisualNovelMode ? "visualNovel" : "default"}
            segmentDisplayMode={isVisualNovelMode ? "current" : "accumulate"}
            logEnabled={isVisualNovelMode}
            logOpenSignal={isVisualNovelMode ? visualNovelLogOpenSignal : undefined}
            logButtonVisibility={isVisualNovelMode ? "none" : "all"}
            onCurrentActorCueChange={isVisualNovelMode ? setVisualNovelActorCue : undefined}
            onChoicePreviewChange={isVisualNovelMode ? setVisualNovelChoicePreview : undefined}
            onChoiceThinkingCueChange={isVisualNovelMode ? setVisualNovelChoiceThinkingCue : undefined}
            pendingChoices={
              isVisualNovelChoiceGenerationPending
                ? pendingChoiceList ?? undefined
                : undefined
            }
            pendingChoiceId={
              isVisualNovelChoiceGenerationPending
                ? selectedChoice?.id
                : undefined
            }
            pendingChoicePrompt={
              isVisualNovelChoiceGenerationPending
                ? pendingChoicePrompt
                : undefined
            }
            suppressWaitingIndicator={isVisualNovelMode}
            panelBackgroundColor={playPanelBackgroundColor}
          />
        ) : (
          <SceneView
            key={isVersioned ? scene.id : undefined}
            scene={scene}
            characters={characters}
            visualAssets={visualAssets}
            isPending={performAction.isPending}
            onSelectChoice={(choiceId) => {
              setVisualNovelChoicePreview(null);
              if (streamingEnabled) {
                handleStreamChoice(choiceId);
              } else {
                rememberPendingChoiceList(scene.choices, choiceId, scene.segments);
                performAction.mutate({
                  type: "choice",
                  choiceId,
                  sceneId: scene.id,
                });
              }
            }}
            onAdvance={() => {
              if (streamingEnabled) {
                handleStreamAdvance();
              } else {
                performAction.mutate({ type: "advance", sceneId: scene.id });
              }
            }}
            onLongPress={openReportModal}
            manualReveal={isVisualNovelMode}
            advanceSignal={isVisualNovelMode ? visualNovelAdvanceSignal : undefined}
            clickAdvanceEnabled={!isVisualNovelMode}
            segmentVariant={isVisualNovelMode ? "visualNovel" : "default"}
            segmentDisplayMode={isVisualNovelMode ? "current" : "accumulate"}
            logEnabled={isVisualNovelMode}
            logOpenSignal={isVisualNovelMode ? visualNovelLogOpenSignal : undefined}
            logButtonVisibility={isVisualNovelMode ? "none" : "all"}
            onCurrentActorCueChange={isVisualNovelMode ? setVisualNovelActorCue : undefined}
            onChoicePreviewChange={isVisualNovelMode ? setVisualNovelChoicePreview : undefined}
            onChoiceThinkingCueChange={isVisualNovelMode ? setVisualNovelChoiceThinkingCue : undefined}
            pendingChoiceId={
              isVisualNovelChoiceGenerationPending
                ? selectedChoice?.id
                : undefined
            }
            pendingChoicePrompt={
              isVisualNovelChoiceGenerationPending
                ? pendingChoicePrompt
                : undefined
            }
            panelBackgroundColor={playPanelBackgroundColor}
          />
        );
        return isVisualNovelMode ? (
          // 初回から背景が未解決のときは placeholder、途中の未解決では直前の背景を維持する。
          // 同一ラベルのシーン遷移では imageUrl が変わらず img src が不変になる (req 4.4)。
          <VisualNovelLayout
            sceneId={scene.id}
            imageUrl={visualNovelImageUrl ?? undefined}
            screenBackgroundColor={playScreenBackgroundColor}
            marionActors={currentMarionActors}
            cutinActor={currentCutinActor}
            hud={renderVisualNovelHudMenu()}
            choicePreview={visualNovelChoicePreview}
            choicePanelPosition={choicePanelPosition}
            isComposing={isVisualNovelComposing}
            composingPreview={visualNovelComposingPreview}
            onAdvanceText={handleVisualNovelAdvanceText}
          >
            {sceneContent}
          </VisualNovelLayout>
        ) : (
          sceneContent
        );
      })()}
      {/* ストリーミング中は StreamingSceneView がインジケータを表示するため LoadingOverlay を非表示にする（要件 4.5）。 */}
      <LoadingOverlay isPending={streamingEnabled ? (performAction.isPending && !streamState.isStreaming) : performAction.isPending} />
      {uiError ? (
        (() => {
          const isDeductionErr = deductionError !== null;
          const retryProps = uiError.retryable
            ? {
                onRetry: () => {
                  if (isDeductionErr) {
                    setDeductionError(null);
                  } else {
                    performAction.reset();
                    streamActions.reset();
                  }
                },
              }
            : {};
          const restartProps = uiError.requiresRestart
            ? { onRestart }
            : {};
          return (
            <ErrorBanner
              error={uiError}
              {...retryProps}
              {...restartProps}
            />
          );
        })()
      ) : null}
      {/* scene-beat-decomposition production 配線 第 4 件: debug=true 時のみ、 現在 scene が
          属する Beat function を indicator として表示する。 reader 通常 view は touch ゼロ、
          debug 経路 (= creator / developer のみ可視) でのみ active。 Beat 未設定 scenario では
          CurrentBeatIndicator 内部で null render し UI 自体を消す silent fallback。 */}
      {debug && (
        <div className="fixed top-2 left-2 z-50 rounded bg-reader-debug-bg/80 px-2 py-1">
          <CurrentBeatIndicator
            baseUrl={apiBaseUrl}
            scenarioId={scenarioId}
            currentSceneId={currentScene.data?.id ?? scene.id}
          />
        </div>
      )}

      {/* バックスクロールパネル（モーダルオーバーレイ） */}
      {backscrollOpen && (
        <ModalFrame
          testId="backscroll-panel"
          title="バックスクロール"
          onClose={closeBackscroll}
        >
          <BackscrollView
            worldlineId={playlineId}
            playlineId={playlineId}
            scenarioId={scenarioId}
            currentSceneId={scene.id}
            characters={characters}
            onForkSuccess={(newWorldlineId) => {
              closeBackscroll();
              onForkSuccess(newWorldlineId);
            }}
          />
        </ModalFrame>
      )}

      {/* scenario-spawn-mvp (Phase γ', Task 7.1) 派生情報通知パネル */}
      {spawnNotificationOpen && (
        <ModalFrame
          testId="scenario-spawn-notification-panel"
          title="あなたの物語の派生"
          onClose={closeSpawnNotification}
        >
          {/* scenario-spawn-creator-credit-ui (Phase γ' closure, Task 2.2):
              modal 中身を ScenarioSpawnNotification (= 上流通知単独) から
              ScenarioLineagePanel (= 上流通知 + 下流派生一覧の双方向 view) に置換。
              modal trigger button / panel framing / data-testid は既存維持。 */}
          <ScenarioLineagePanel
            scenarioId={scenarioId}
            scenarioTitle={displayScenario?.title ?? ""}
          />
        </ModalFrame>
      )}

      {/* scene-summary-feed (Phase γ, Task 7.1) cross-reader summary feed パネル */}
      {summaryFeedOpen && (
        <ModalFrame
          testId="scene-summary-feed-panel"
          title="世界線の足跡"
          onClose={closeSummaryFeed}
        >
          <SceneSummaryFeed scenarioId={scenarioId} />
        </ModalFrame>
      )}

      {/* worldline-palimpsest (Phase 3-1, Task 6.1) palimpsest 2D view モーダル */}
      {worldlinePalimpsestEnabled && (
        <PalimpsestModal
          open={palimpsestOpen}
          onClose={closePalimpsest}
          scenarioId={scenarioId}
          currentWorldlineId={playlineId}
          onForkSuccess={(newWorldlineId) => {
            closePalimpsest();
            onForkSuccess(newWorldlineId);
          }}
        />
      )}

      {/* character-unlock (Task 4.1): キャラ図鑑モーダル + 解放 toast。
          - playlineId が null のとき modal は render しない (open === false で内部 fetch 抑制 + DOM ゼロ、Req 5.5)。
          - UnlockToast は promotedCharacters.length === 0 で内部的に DOM ゼロを返す (Req 4.4 / 4.5)。 */}
      {playlineId !== null && (
        <CharacterCodexView
          open={codexOpen}
          onClose={closeCodex}
          playlineId={playlineId}
          scenarioId={scenarioId}
          apiBaseUrl={apiBaseUrl}
        />
      )}
      <UnlockToast
        promotedCharacters={promotedCharacters}
        onDismiss={dismissPromotion}
      />

      {/* interactive-deduction (Phase 3-3, Task 6.6): 駆け引きパネル。scene.contradictions が
          non-empty のときだけモーダル表示。forcedBadEnding 応答 → palimpsest 起動誘導 (button)。
          plain 失敗 (success=false && !forcedBadEnding) では panel が残り player は再挑戦できる。 */}
      {scene !== null && (
        <DeductionHost
          enabled={interactiveDeductionEnabled}
          playlineId={playlineId}
          sceneId={scene.id}
          scene={scene}
          attempts={deductionAttempts}
          style="class-trial"
          onResolved={(result) => {
            // HP を追跡更新 (成功 / 失敗どちらでも remainingAttempts を反映)
            setDeductionAttempts(result.remainingAttempts);
            if (result.forcedBadEnding) {
              showForcedBadEnding();
            } else if (result.success) {
              // 成功 → 次 scene へ
              void currentScene.refetch();
            }
            // plain 失敗は refetch せず (scene 維持、panel も維持)
          }}
          onError={(err) => {
            setDeductionError(err);
          }}
          onActiveChange={setDeductionActive}
        />
      )}

      {/* interactive-deduction (follow-up polish): forcedBadEnding=true 応答後の
          palimpsest 誘導 button (Req 4.4 "prominent button 表示" 準拠)。
          worldlinePalimpsestEnabled=false のときは CTA なしの通知のみ (fallback)。 */}
      {forcedBadEndingShown && (
        <div
          data-testid="forced-bad-ending-cta"
          className="fixed left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-4 bg-red-900/95 border-2 border-red-500 rounded-lg shadow-2xl"
          style={{
            // safe-area-inset を加味した bottom。 iOS home indicator / Android nav bar
            // 環境で自動的に上にずれる (= 計算式: inset + 1.5rem の 6)。
            bottom: "calc(env(safe-area-inset-bottom) + 1.5rem)",
          }}
        >
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-bold text-red-100">
              推理に失敗しました
            </p>
            <p className="text-xs text-red-200/80">
              {worldlinePalimpsestEnabled
                ? "別の世界線で再挑戦できます"
                : "新しいプレイラインを始めてください"}
            </p>
          </div>
          {worldlinePalimpsestEnabled ? (
            <button
              type="button"
              onClick={() => {
                hideForcedBadEnding();
                openPalimpsest();
              }}
              data-testid="forced-bad-ending-palimpsest-btn"
              className="min-h-11 px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-bold rounded"
            >
              別世界線へ
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                hideForcedBadEnding();
                onRestart();
              }}
              data-testid="forced-bad-ending-restart-btn"
              className="min-h-11 px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-bold rounded"
            >
              最初から
            </button>
          )}
        </div>
      )}

      {/* mobile-pwa Phase 4.3: 長押しで起動する scene 通報 modal。 SceneView.onLongPress
          経由で openReportModal が発火し、 ReportModal を mount。 close 時には
          closeReportModal で DOM から外れて fetch も止まる (= 既存 modal doctrine 踏襲)。 */}
      {reportModalOpen && (
        <ReportModal
          baseUrl={apiBaseUrl}
          targetType="scene"
          targetId={scene.id}
          targetLabel={`scene ${scene.id}`}
          onClose={closeReportModal}
        />
      )}

      {/* playline-persistence: scene sync で 409 version conflict 検知時の解決 modal。
          「クラウドを使う」 で onRestart (= cloud playlineId 再読込)、 「この端末を使う」 で
          force overwrite sync。 conflict null で render されない (= 通常時は dormant)。 */}
      {playlineSync.conflict !== null && (
        <ConflictResolutionModal
          playlineId={playlineSync.conflict.playlineId}
          cloud={playlineSync.conflict.cloud}
          local={playlineSync.conflict.local}
          onUseCloud={() => playlineSync.resolveUseCloud(() => onRestart())}
          onUseLocal={() => playlineSync.resolveUseLocal()}
          onClose={() => playlineSync.dismissConflict()}
        />
      )}

      {/* 世界線ツリーパネル（モーダルオーバーレイ） */}
      {treeOpen && (
        <ModalFrame
          testId="worldline-tree-panel"
          title="世界線ツリー"
          onClose={closeTree}
        >
          <WorldlineTreeView
            scenarioId={scenarioId}
            currentWorldlineId={playlineId}
            onForkRequest={(worldlineId) => {
              // ツリービューの「ここから分岐」は BackscrollView で選択肢を選ぶ設計のため
              // ツリービューを閉じてバックスクロールを開く（要件 5.1）。
              // 現時点では同一 worldlineId のバックスクロールを開く。
              closeTree();
              // worldlineId が現在の playlineId と同じ場合のみパネルを開く。
              // 別 worldlineId の場合は将来的に対応する。
              if (worldlineId === playlineId) {
                openBackscroll();
              }
            }}
            onForkSuccess={(newWorldlineId) => {
              closeTree();
              onForkSuccess(newWorldlineId);
            }}
          />
        </ModalFrame>
      )}
    </div>
  );
}
